import React, { useEffect, useMemo, useState } from "react";
import {
    PersonalProductRecommendationBuilder,
    RecentlyViewedProductsBuilder,
    PopularProductsBuilder,
    PurchasedWithMultipleProductsBuilder,
    SimilarProductsProductBuilder,
    ProductResult,
} from "@relewise/client";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import { addWebUserGroupsFilter } from "@danishagro/shared/src/helpers/relewiseFilters.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useCart } from "@danishagro/shared/src/contexts/cart/cart.context";
import { DA_ProductItemSliderSiloAndHouse } from "@organisms/ProductItemSliderSiloAndHouse";

export interface BlockProductsProps {
    headline: string;
    sliderType:
        | "Latest"
        | "Popular"
        | "Personal"
        | "PurchasedWithMultiple"
        | "Similar"
        | "Related"
        | "Spare Parts";
    productId?: string;
    relatedProductIds?: string[];
    sparePartProductIds?: string[];
    numberOfProducts?: number;
}

export const BlockProductsSiloAndHouse: React.FC<BlockProductsProps> = ({
    headline,
    sliderType,
    productId,
    relatedProductIds,
    sparePartProductIds,
    numberOfProducts = 0,
}) => {
    const { getRecommender, mapProduct, relewiseUser, getRelatedProducts, getSparepartProducts } =
        useRelewise();
    const { customerNumber, cvrAndCustomerNumbers } = useAppData();
    const { orderLines } = useCart();
    const [products, setProducts] = useState<ProductResult[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const numberOfProductsShown = numberOfProducts === 0 ? 6 : numberOfProducts;

    // ✅ Memoize settings to prevent unnecessary re-renders
    const settings = useMemo(
        () => ({
            language: "da",
            currency: "DKK",
            displayedAtLocation: globalThis.pageAlias || globalThis.alias,
            user: relewiseUser(),
        }),
        [relewiseUser]
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const recommender = getRecommender();
            if (!recommender) {
                setLoading(false);
                return;
            }

            let builder;
            try {
                switch (sliderType) {
                    case "Popular": {
                        const popularBuilder = new PopularProductsBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(6)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .basedOn("MostViewed")
                            .sinceMinutesAgo(144000)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            popularBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = popularBuilder.build();
                        const result = await recommender.recommendPopularProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Personal": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        const personalBuilder = new PersonalProductRecommendationBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(10)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            personalBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = personalBuilder.build();
                        const result = await recommender.recommendPersonalProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Latest": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        const recentlyViewedBuilder = new RecentlyViewedProductsBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(6)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            recentlyViewedBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = recentlyViewedBuilder.build();
                        const result = await recommender.recentlyViewedProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "PurchasedWithMultiple": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        // On PDP use the viewed product; elsewhere use cart items
                        const cartProducts = productId
                            ? [{ productId }]
                            : orderLines?.map((line) => ({ productId: line.productId }));

                        if (!cartProducts || cartProducts.length === 0) {
                            setLoading(false);
                            return;
                        }

                        const purchasedWithBuilder = new PurchasedWithMultipleProductsBuilder(
                            settings
                        )
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(numberOfProductsShown)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(true)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false)
                            .addProducts(cartProducts);

                        if (globalThis.useCustomerGroups) {
                            purchasedWithBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }

                        builder = purchasedWithBuilder.build();
                        const result = await recommender.recommendPurchasedWithMultipleProducts(
                            builder
                        );
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Similar": {
                        // Need a product ID to find similar products
                        if (!productId) {
                            setLoading(false);
                            return;
                        }

                        const similarBuilder = new SimilarProductsProductBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(numberOfProductsShown)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false)
                            .product({ productId });

                        if (globalThis.useCustomerGroups) {
                            similarBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }

                        builder = similarBuilder.build();
                        const result = await recommender.recommendSimilarProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Related": {
                        if (!productId) {
                            setLoading(false);
                            return;
                        }
                        const result = await getRelatedProducts(
                            productId,
                            relatedProductIds || [],
                            numberOfProductsShown
                        );
                        setProducts(result ?? []);
                        break;
                    }

                    case "Spare Parts": {
                        if (!sparePartProductIds || sparePartProductIds.length === 0) {
                            setLoading(false);
                            return;
                        }

                        const sparepartProducts = await getSparepartProducts(sparePartProductIds);
                        setProducts(sparepartProducts);
                        break;
                    }

                    default:
                        setLoading(false);
                        return;
                }
            } catch (error) {
                console.error(`API Error (${sliderType}):`, error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [
        sliderType,
        getRecommender,
        settings,
        customerNumber,
        cvrAndCustomerNumbers,
        orderLines,
        productId,
        relatedProductIds,
        sparePartProductIds,
        numberOfProductsShown,
        getRelatedProducts,
        getSparepartProducts,
    ]);

    // Display a loading indicator while fetching data
    if (loading) return <div />;

    // Return null if no products exist
    if (!products.length) return null;

    return (
        <DA_ProductItemSliderSiloAndHouse
            title={headline}
            items={products.map((product) => mapProduct(product))}
        />
    );
};
